import { gql } from '@apollo/client';
import * as React from 'react';
import { CustomerInput } from 'src/graphql/__generated__/globalTypes';
import { Button } from 'src/shadcn/components/ui/button';
import { DialogFooter } from 'src/shadcn/components/ui/dialog';
import {
  useSearchCustomerForNewCustomerCreationForJobQuery,
  useSetCustomerForJobCustomerSelectionMutation,
} from './__generated__/JobSelectCustomerModalFooter.generated';
import { useHotToast } from 'src/utils/useHotToast';
import { AlertCircle, RotateCw } from 'lucide-react';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from 'src/shadcn/components/ui/hover-card';
import { CustomerHoverCardContent } from 'src/Components/PaymentsPage/summaries/WorkOrdersTable/CustomerHoverCardContent';
import { notEmpty, WithoutTypename } from 'src/utils/utils';
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from 'src/shadcn/components/ui/alert';
import { Separator } from 'src/shadcn/components/ui/separator';
import { Customer } from './CustomerType';

export interface IJobSelectCustomerModalFooterProps {
  onSelectCustomer: (c: Customer) => void;
  newCustomer: Partial<Customer>;
}

export const JobSelectCustomerModalFooter: React.FunctionComponent<
  IJobSelectCustomerModalFooterProps
> = ({ newCustomer, ...props }) => {
  const { saveCustomer, isSavingCustomer } = useSaveCustomer(
    newCustomer,
    newCustomer2 => {
      props.onSelectCustomer(newCustomer2);
    }
  );
  const { isSearchingCustomers, searchResult } = useSearchCustomers(
    newCustomer.phoneNumber || ''
  );
  return (
    <>
      {Boolean(searchResult?.length) && (
        <Alert className='mb-5'>
          <AlertCircle className='size-4' />
          <AlertTitle>Customer already exists.</AlertTitle>
          <AlertDescription>
            Customer with this phone number already exists.
          </AlertDescription>
          <div className='flex flex-col gap-3 pt-5'>
            {searchResult?.map((c, i) => {
              const customerText =
                [c?.firstName, c?.lastName].filter(notEmpty).join(' ') ||
                'Customer';
              return (
                <React.Fragment key={c.id}>
                  <div className='flex items-center justify-between'>
                    <HoverCard>
                      <HoverCardTrigger
                        className='truncate text-lg md:text-sm'
                        data-testid='work-order-sidebar-customer-title'
                      >
                        <span>{customerText}</span>
                      </HoverCardTrigger>
                      <HoverCardContent className='w-80'>
                        <CustomerHoverCardContent data={c} />
                      </HoverCardContent>
                    </HoverCard>
                    <Button
                      variant={'ghost'}
                      size={'sm'}
                      onClick={() => {
                        props.onSelectCustomer(c);
                      }}
                    >
                      <span>Select</span>
                    </Button>
                  </div>
                  {i + 1 !== searchResult?.length && <Separator />}
                </React.Fragment>
              );
            })}
          </div>
        </Alert>
      )}

      <DialogFooter>
        <Button
          disabled={isSearchingCustomers || (searchResult?.length || 0) > 0}
          onClick={() => {
            const customerInput: CustomerInput = {
              id: newCustomer.id || '',
              firstName: newCustomer.firstName || '',
              lastName: newCustomer.lastName || '',
              email: newCustomer.email || null,
              phoneNumber: newCustomer.phoneNumber || null,
              secondaryPhoneNumber: newCustomer.secondaryPhoneNumber || null,
              companyName: newCustomer.companyName || null,
              shippingAddress:
                newCustomer.shippingAddress?.addressLine1 &&
                newCustomer.shippingAddress?.country &&
                newCustomer.shippingAddress?.locality &&
                newCustomer.shippingAddress?.postalCode
                  ? {
                      addressLine1: newCustomer.shippingAddress.addressLine1,
                      country: newCustomer.shippingAddress.country,
                      locality: newCustomer.shippingAddress.locality,
                      postalCode: newCustomer.shippingAddress.postalCode,
                    }
                  : undefined,
            };
            saveCustomer(customerInput);
          }}
        >
          <span>Save</span>
          {isSavingCustomer && (
            <RotateCw className='ml-2 h-4 w-4 animate-spin' />
          )}
        </Button>
      </DialogFooter>
    </>
  );
};

function useSearchCustomers(customerPhoneNumber: string) {
  const toast = useHotToast();
  const { data, loading } = useSearchCustomerForNewCustomerCreationForJobQuery({
    onError: e => {
      toast.error(e.message);
    },
    variables: {
      searchTerm: customerPhoneNumber,
    },
    skip: customerPhoneNumber.length !== 10,
  });
  return {
    searchResult: data?.searchCustomersBySearchTerm.customers,
    isSearchingCustomers: loading,
  };
}

const SEARCH_CUSTOMERS_QUERY = gql`
  query SearchCustomerForNewCustomerCreationForJob($searchTerm: String!) {
    searchCustomersBySearchTerm(searchTerm: $searchTerm) {
      customers {
        createdAt
        id
        firstName
        lastName
        email
        phoneNumber
        secondaryPhoneNumber
        companyName
      }
    }
  }
`;

function useSaveCustomer(
  oldCustomer: Partial<Customer>,
  onSaveCustomer: (newCustomer: Customer) => void
) {
  const toast = useHotToast();
  const [saveCustomer, { loading: isSavingCustomer }] =
    useSetCustomerForJobCustomerSelectionMutation({
      onError: error => {
        toast.error(error.message);
      },
      fetchPolicy: 'no-cache',
    });
  return {
    isSavingCustomer,
    saveCustomer: (customerInput: CustomerInput) => {
      saveCustomer({
        variables: {
          customer: customerInput,
        },
        optimisticResponse: {
          upsertCustomer: true,
        },
        onCompleted: () => {
          onSaveCustomer({
            ...oldCustomer,
            ...customerInput,
            createdAt: oldCustomer.createdAt || new Date().toISOString(),
          });
        },
      });
    },
  };
}

const SET_CUSTOMER_FOR_CUSTOMER_PAGE = gql`
  mutation SetCustomerForJobCustomerSelection($customer: CustomerInput!) {
    upsertCustomer(customer: $customer)
  }
`;
